import { useEffect } from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import DocsSidebar from "@/components/app/DocsSidebar";
import { getDocsByCategory } from "@/content/docs";
import { ArrowRight, Clock, BookOpen, Rss, Play } from "lucide-react";

/**
 * /docs — Resource hub index. Lists every help article grouped by category.
 * Pulls from /content/docs.js so it's fully static, crawlable, and zero-API.
 */
export default function Docs() {
  const groups = getDocsByCategory();

  useEffect(() => {
    document.title = "Help docs — ArloSEO";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "How-to guides, integration walkthroughs, and best practices for ArloSEO — the AI SEO workspace.",
      );
    }
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="docs-page">
        {/* Header */}
        <div className="border-b border-border pb-10 flex items-end justify-between flex-wrap gap-6">
          <div>
            <span className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
              // help center
            </span>
            <h1 className="font-semibold tracking-tight mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Everything you need to{" "}
              <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
                ship with Arlo
              </span>
              .
            </h1>
            <p className="mt-4 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              Short, opinionated guides for every corner of ArloSEO. Hand‑written by humans, occasionally proofread by Arlo.
            </p>
          </div>
          <div className="flex items-center gap-2 font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground">
            <OtterIcon size={18} className="text-foreground" />
            <span>{getDocsByCategory().reduce((acc, g) => acc + g.items.length, 0)} articles · v1</span>
          </div>
        </div>

        {/* Layout: sidebar + grid of articles */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-[18rem,1fr] gap-12">
          <DocsSidebar />

          <div className="space-y-14" data-testid="docs-grid">
            {groups.map((g) => (
              <section key={g.id} data-testid={`docs-category-${g.id}`}>
                <div className="flex items-center gap-3 mb-5">
                  <BookOpen className="w-4 h-4 text-foreground" />
                  <h2 className="font-semibold tracking-tight text-xl">{g.label}</h2>
                  <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    {g.items.length} {g.items.length === 1 ? "article" : "articles"}
                  </span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-4">
                  {g.items.map((d, i) => (
                    <li key={d.slug}>
                      <Link
                        to={`/docs/${d.slug}`}
                        data-testid={`docs-card-${d.slug}`}
                        className="group block h-full border border-border bg-white p-5 hover:border-foreground transition-colors"
                      >
                        <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {d.reading_minutes} min read
                          {d.videoUrl && (
                            <span
                              className="inline-flex items-center gap-1 border border-foreground bg-foreground text-background px-1.5 py-0.5"
                              data-testid={`docs-video-badge-${d.slug}`}
                            >
                              <Play className="w-2.5 h-2.5 fill-current" /> video
                            </span>
                          )}
                        </div>
                        <h3 className="font-semibold tracking-tight mt-3 text-lg leading-snug group-hover:underline underline-offset-4 decoration-2">
                          {d.title}
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">
                          {d.excerpt}
                        </p>
                        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium">
                          Read guide
                          <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {/* What's new (changelog teaser) */}
            <Link
              to="/docs/changelog"
              data-testid="docs-changelog-teaser"
              className="group block border border-foreground bg-white p-6 hover:bg-foreground hover:text-background transition-colors"
            >
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-background/60">
                    // release notes
                  </div>
                  <h3 className="font-semibold tracking-tight mt-2 text-lg leading-snug">
                    What&apos;s new — every shipping moment, auto-generated.
                  </h3>
                </div>
                <div className="flex items-center gap-1.5 text-sm font-medium whitespace-nowrap">
                  <Rss className="w-3.5 h-3.5" /> View
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </div>
            </Link>

            {/* Contact card */}
            <div className="border border-foreground bg-foreground text-background p-8 relative overflow-hidden" data-testid="docs-help-card">
              <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
              <div className="relative">
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">
                  // didn&apos;t find what you needed?
                </span>
                <h3 className="font-semibold tracking-tight text-2xl mt-3 leading-tight">
                  Arlo&apos;s humans are one email away.
                </h3>
                <p className="mt-3 text-background/70 max-w-xl text-sm">
                  Email <span className="underline">support@arloseo.com</span> with as much detail as you can — screenshots welcome — and we&apos;ll reply within one business day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
